import { CarouselController } from '../../../../../components/Carousel';
import classNames from 'classnames';
import ImageGallery from '../../../../../components/ImageGallery/ImageGallery';
import React from 'react';
import styles from './ImageGallery.module.scss';
import * as types from '../../../../../types';
import { showNanoVariant } from '../../../../../components/NanoBanner/NanoBanner';

const DESKTOP_IMAGE_GALLERY_SIZE = 7;

const SERIES_THREE_PLUS_IMAGES: types.IImage[] = [
  {
    type: 'image',
    url: '/product_images/smart-collar-v3b/activity.jpg',
    descriptors: ['1500w', '3000w', '4000w'],
  },
  {
    type: 'image',
    url: '/product_images/smart-collar-v3b/eating.jpg',
    descriptors: ['1500w', '3000w', '4000w'],
  },
  {
    type: 'image',
    url: '/product_images/smart-collar-v3b/scratching.jpg',
    descriptors: ['1500w', '3000w', '4000w'],
  },
  {
    type: 'image',
    url: '/product_images/smart-collar-v3b/sleeping.jpg',
    descriptors: ['1500w', '3000w', '4000w'],
  },
];

interface DesktopImageGalleryProps {
  carouselControllerRef?: React.MutableRefObject<CarouselController | undefined>;
  selectedVariant?: types.IVariant;
  onChange?: (index: number) => void;
  children?: React.ReactNode;
  /** Dynamic images from API - if provided, overrides hardcoded images */
  dynamicImages?: (types.IImage | types.IVideo)[] | null;
  /** Replaces the layflat the gallery opens with. */
  heroImage?: types.IImage;
}

export function imageUrlForVariant(variant: types.IVariant, index: number) {
  return `/product_images/smart-collar-v3b/${variant.options.color.replace(/[\s/]/g, '_')}${index}.jpg`;
}

export function lifestyleImageUrlForVariant(variant: types.IVariant) {
  return `/product_images/smart-collar-v3/${variant.options.color.replace(/[\s/]/g, '_')}-lifestyle.jpg`;
}

export function layflatImageUrlForVariant(variant: types.IVariant) {
  return `/product_images/smart-collar-v3/circle-${variant.options.color.replace(/[\s/]/g, '_')}.png`;
}

export function DesktopImageGallery({
  carouselControllerRef,
  selectedVariant,
  onChange,
  children,
  dynamicImages,
  heroImage,
}: DesktopImageGalleryProps) {
  // Multiple images depend on the variant
  const productImages: types.IImage[] = [3].map((index) => ({
    type: 'image',
    url: imageUrlForVariant(selectedVariant!, index),
    descriptors: ['1500w', '3000w', '4000w'],
  }));

  // Maintain a maximum of 7 images in carousel
  const defaultImages = [
    heroImage ?? {
      type: 'image',
      url: layflatImageUrlForVariant(selectedVariant!),
      descriptors: ['1500w', '3000w', '4000w'],
    },
    ...productImages,
    ...SERIES_THREE_PLUS_IMAGES,
  ].slice(0, DESKTOP_IMAGE_GALLERY_SIZE);

  // Use dynamic images if provided, otherwise use default
  const showNanoMedia = showNanoVariant();
  let media: (types.IImage | types.IVideo)[];

  if (showNanoMedia) {
    media = [
      ...productImages,
      {
        type: 'video',
        url: `/videos/sizzle-vid.mp4`,
        thumbnail: {
          type: 'image',
          url: `/videos/sizzle-vid-thumbnail.jpg`,
        },
      },
    ];
  } else {
    media = dynamicImages ?? defaultImages;
  }

  return (
    <div className={classNames(styles.imageGalleryContainer, styles.desktopImageGallery)}>
      {children}
      <ImageGallery
        parentCarouselControllerRef={carouselControllerRef}
        media={media}
        pageDots={true}
        disableAnimation={true}
        friction={0.4}
        onChange={onChange}
      />
    </div>
  );
}
